import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { InventoryUntestedLotsDialog } from "../../../../components"
import type { InventoryLotReference } from "../../../../types"
import { useAdjustmentLotSelection, useAdjustmentLotValidation } from "../../hooks"
import { useCreateLotCorrectionAdjustment } from "../../hooks/useCreateLotCorrectionAdjustment"
import { type LotCorrectionAdjustmentFormValues, lotCorrectionAdjustmentFormSchema } from "../../schemas"
import {
  filterChangedExistingLots,
  filterLotCorrectionAdjustmentDuplicateLotIndexes,
  getAdjustmentLotDefaultFormValues,
  toLotCorrectionAdjustmentCreateInput,
  toLotCorrectionAdjustmentExistingLotFormValues,
} from "../../utils"
import { AdjustmentLotSelection } from "../AdjustmentLotSelection"
import { AdjustmentTypeHeader } from "../AdjustmentTypeHeader"
import { AdjustmentUntestedLotsConfirmationDialog } from "../AdjustmentUntestedLotsConfirmationDialog"

import { LotCorrectionAdjustmentFormDetails } from "./LotCorrectionAdjustmentFormDetails"
import { LotCorrectionAdjustmentFormLots } from "./LotCorrectionAdjustmentFormLots"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { InventoryAdjustmentType, type InventoryByProductWarehouse, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface LotCorrectionAdjustmentFormDialogProps {
  product: Product
  warehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onClose: () => void
  onSuccess: () => void
}

export const LotCorrectionAdjustmentFormDialog: FC<LotCorrectionAdjustmentFormDialogProps> = ({
  product,
  warehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onClose,
  onSuccess,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [untestedExistingLots, setUntestedExistingLots] = useState<InventoryLotReference[]>([])
  const [untestedNewLots, setUntestedNewLots] = useState<InventoryLotReference[]>([])
  const { validateLotsTested } = useAdjustmentLotValidation()
  const { createAdjustment } = useCreateLotCorrectionAdjustment()

  const form = useForm<LotCorrectionAdjustmentFormValues>({
    resolver: zodResolver(lotCorrectionAdjustmentFormSchema),
    defaultValues: {
      warehouseAreaId,
      comment: "",
      document: undefined,
      existingLots: [],
      newLots: [],
    },
    mode: "onChange",
  })
  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    formState: { isValid, isDirty, isSubmitting, errors },
  } = form
  const selectedWarehouseAreaId = watch("warehouseAreaId")

  const { fields: existingLotFields } = useFieldArray({ control, name: "existingLots" })
  const { fields: newLotFields, append: appendLot, remove: removeLot } = useFieldArray({ control, name: "newLots" })

  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    sortBy,
    sortDirection,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionReset,
  } = useAdjustmentLotSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: selectedWarehouseArea!.externalSystemId!,
  })

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  useEffect(() => {
    reset(
      { ...getValues(), existingLots: selectedLots.map(toLotCorrectionAdjustmentExistingLotFormValues), newLots: [] },
      { keepDefaultValues: false }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLots])

  const existingLots = useWatch({ control, name: "existingLots" })
  const newLots = useWatch({ control, name: "newLots" })
  const sourceRawInventoryId = existingLots[0]?.rawInventoryId ?? ""

  const duplicateLotIndexes = useMemo(
    () => filterLotCorrectionAdjustmentDuplicateLotIndexes(existingLots ?? [], newLots ?? []),
    [existingLots, newLots]
  )

  const canSubmit =
    isValid &&
    isDirty &&
    !isSubmitting &&
    !errors.document &&
    selectedLots.length > 0 &&
    !duplicateLotIndexes.existingLots.size &&
    !duplicateLotIndexes.newLots.size

  const handleWarehouseChange = () => {
    handleSelectionReset()
  }

  const handleLotAdd = () => {
    appendLot({ ...getAdjustmentLotDefaultFormValues(product), rawInventoryId: sourceRawInventoryId })
  }

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const submitForm = async (formValues: LotCorrectionAdjustmentFormValues) => {
    const isSuccess = await createAdjustment(
      toLotCorrectionAdjustmentCreateInput(formValues, product.productId),
      formValues.comment,
      formValues.document
    )

    if (isSuccess) onSuccess()
  }

  const handleValidateAndSubmit = async (formValues: LotCorrectionAdjustmentFormValues) => {
    const changedExistingLots = filterChangedExistingLots(formValues.existingLots)
    let untestedBlockingLots = await validateLotsTested(changedExistingLots, product)

    if (untestedBlockingLots.length > 0) {
      setUntestedExistingLots(untestedBlockingLots)
      return
    }

    untestedBlockingLots = await validateLotsTested(formValues.newLots, product)

    if (untestedBlockingLots.length > 0) {
      setUntestedNewLots(untestedBlockingLots)
      return
    }

    await submitForm(formValues)
  }

  const handleUntestedNewLotsConfirm = () => {
    setUntestedNewLots([])
    submitForm(getValues())
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedExistingLots.length > 0) {
    return (
      <InventoryUntestedLotsDialog untestedLots={untestedExistingLots} onClose={() => setUntestedExistingLots([])} />
    )
  }

  if (untestedNewLots.length > 0) {
    return (
      <AdjustmentUntestedLotsConfirmationDialog
        untestedLots={untestedNewLots}
        onClose={() => setUntestedNewLots([])}
        onConfirm={handleUntestedNewLotsConfirm}
      />
    )
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='inventory.lot-correction-adjustment-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleCancel} />
      <FormProvider {...form}>
        <AdjustmentTypeHeader
          adjustmentType={InventoryAdjustmentType.LotBatchCorrection}
          product={product}
          warehouseOptions={warehouseOptions}
          onWarehouseChange={handleWarehouseChange}
        />
        <DialogContent className='grid flex-1 grid-cols-[1fr_368px] gap-x-6 overflow-hidden px-10 pb-10 pt-6'>
          <Box className='flex min-w-0 flex-col gap-y-6 overflow-hidden'>
            <AdjustmentLotSelection
              palletGroups={palletGroups}
              isFetching={isFetching}
              selectedLotIds={selectedLotIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              uomName={uomName}
              selectionMode='singlePallet'
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
            />
            <LotCorrectionAdjustmentFormLots
              selectedLots={selectedLots}
              product={product}
              uomName={uomName}
              existingLotFields={existingLotFields}
              newLotFields={newLotFields}
              duplicateLotIndexes={duplicateLotIndexes}
              onLotAdd={handleLotAdd}
              onLotRemove={removeLot}
            />
          </Box>
          <LotCorrectionAdjustmentFormDetails />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='inventory.lot-correction-adjustment-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit(handleValidateAndSubmit)}
          data-testid='inventory.lot-correction-adjustment-form-dialog.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
